import { createContext, ReactNode, useContext, useReducer } from 'react';
import { PlayableInvoice } from '@/enterprise/data/usePlayableInvoices';
import { EditError } from './useInvoiceEdits';

type SelectionMode = 'none' | 'some' | 'page' | 'all';

interface EditInvoicesState {
  loading: boolean;
  selected: PlayableInvoice[];
  selectionMode: SelectionMode;
  progress: { current: number; total: number } | null;
  errors: EditError[];
}

export type EditInvoicesAction =
  | { type: 'TOGGLE_SELECTED'; payload: { invoice: PlayableInvoice } }
  | { type: 'RESET_SELECTED' }
  | { type: 'SET_LOADING'; payload: { loading: boolean } }
  | { type: 'SELECT_ALL_PAGE'; payload: { invoices: PlayableInvoice[] } }
  | { type: 'SELECT_ALL' }
  | { type: 'SET_PROGRESS'; payload: { current: number; total: number } }
  | { type: 'SET_ERRORS'; payload: { errors: EditError[] } }
  | { type: 'CLEAR_ERRORS' };

interface EditInvoicesProviderProps {
  children: ReactNode;
}

const editInvoicesReducer = (state: EditInvoicesState, action: EditInvoicesAction): EditInvoicesState => {
  switch (action.type) {
    case 'TOGGLE_SELECTED': {
      const { invoice } = action.payload;
      const newSelected = state.selected.some((i) => i.voucherId === invoice.voucherId)
        ? state.selected.filter((i) => i.voucherId !== invoice.voucherId)
        : [...state.selected, invoice];

      return {
        ...state,
        selected: newSelected,
        selectionMode: newSelected.length === 0 ? 'none' : 'some',
      };
    }

    case 'RESET_SELECTED': {
      return {
        ...state,
        selected: [],
        selectionMode: 'none',
      };
    }

    case 'SET_LOADING': {
      const { loading } = action.payload;

      return {
        ...state,
        loading,
        ...(loading === false && { progress: null }),
      };
    }

    case 'SET_PROGRESS': {
      const { current, total } = action.payload;

      return {
        ...state,
        progress: { current, total },
      };
    }

    case 'SET_ERRORS': {
      const { errors } = action.payload;

      return {
        ...state,
        errors,
      };
    }

    case 'CLEAR_ERRORS': {
      return {
        ...state,
        errors: [],
      };
    }

    case 'SELECT_ALL_PAGE': {
      const { invoices } = action.payload;
      const newSelected = [...state.selected];

      invoices.forEach((invoice) => {
        if (!newSelected.some((selected) => selected.voucherId === invoice.voucherId)) {
          newSelected.push(invoice);
        }
      });

      return {
        ...state,
        selected: newSelected,
        selectionMode: 'page',
      };
    }

    case 'SELECT_ALL': {
      return {
        ...state,
        selected: [],
        selectionMode: 'all',
      };
    }

    default:
      return state;
  }
};

export interface EditInvoicesContextValue {
  dispatch: React.Dispatch<EditInvoicesAction>;
  state: EditInvoicesState;
}

const EditInvoicesContext = createContext<EditInvoicesContextValue | null>(null);

const initialState: EditInvoicesState = {
  loading: false,
  selected: [],
  selectionMode: 'none',
  progress: null,
  errors: [],
};

export const EditInvoicesProvider = ({ children }: EditInvoicesProviderProps) => {
  const [state, dispatch] = useReducer(editInvoicesReducer, initialState);

  const value = {
    state,
    dispatch,
  };

  return <EditInvoicesContext.Provider value={value}>{children}</EditInvoicesContext.Provider>;
};

export const useEditInvoicesContext = () => {
  const context = useContext(EditInvoicesContext);

  if (!context) {
    throw new Error('useEditInvoices must be used within an EditInvoicesProvider');
  }

  return context;
};
